"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadDsnTransformModuleGenerator = void 0;
exports.pinPositionMapWithLiveFallback = pinPositionMapWithLiveFallback;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const alphaDesignatorToConstant_1 = require("../../../../common/alphaDesignatorToConstant");
const TransformModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModule");
const TransformMultiInstanceDisplayModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformMultiInstanceDisplayModule");
const TransformMultiInstanceSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformMultiInstanceSchematic");
const TransformModulePinConnection_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModulePinConnection");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const TransformProjectModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProjectModule");
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformObject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformObject");
const orcadLayers_1 = require("../../../orcadLayers");
const OrcadDsnTransformComponentGenerator_1 = require("./OrcadDsnTransformComponentGenerator");
const OrcadDsnPhysicalParts_1 = require("./OrcadDsnPhysicalParts");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const orcadPinRefs_1 = require("./orcadPinRefs");
const OrcadDsnSectionNumbering_1 = require("./OrcadDsnSectionNumbering");
const OrcadDsnSectionEmission_1 = require("./OrcadDsnSectionEmission");
const ModelText_1 = require("../../../../../ModelText");
function pinPositionMapWithLiveFallback(cachePinPositionMap, portInsts) {
    return cachePinPositionMap && cachePinPositionMap.size > 0 ?
        cachePinPositionMap
        : new Map(portInsts.map((portInst) => [
            portInst.Position,
            portInst.Name === '' ? String(portInst.Position + 1) : (portInst.Name ?? String(portInst.Position + 1)),
        ]));
}
function symbolPinPositionsOfView(view) {
    const map = new Map();
    for (const pin of [...(view.PinScalar ?? []), ...(view.PinBus ?? [])]) {
        map.set(pin.Position, { HotptX: pin.HotptX, HotptY: pin.HotptY });
    }
    return map;
}
class OrCadDsnTransformModuleGenerator {
    constructor() {
        this.recordedPinMismatchDiagnosticsEmitted = 0;
        this.runsFor = [TransformSchematic_1.TransformSchematic];
        this.physicalPartResultsEmitted = new WeakSet();
        this.physicalPartResolver = new OrcadDsnPhysicalParts_1.OrcadDsnPhysicalPartResolver();
        this.emptyDesignatorSelections = new Map();
    }
    static { this.RECORDED_PIN_MISMATCH_DIAGNOSTIC_LIMIT = 25; }
    *generate(parent, _rootContext) {
        const page = parent.hierarchyEntry.page;
        const instance = parent.instance;
        yield* this.reportPhysicalParts(parent, parent.hierarchyEntry.design);
        for (const partInst of page.PartInst ?? []) {
            if (this.isVerifiedReferenceOnlyHierarchyPart(parent, partInst)) {
                if ((0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(partInst) && !this.expandsIntoSubSheet(parent, partInst)) {
                    const keepsPhysicalPart = (0, OrcadDsnUtils_1.unresolvedBlockIsPhysicalPart)(this.expandsIntoSubSheet(parent, partInst), parent.hierarchyEntry.design, partInst);
                    yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                        type: 'orcad-dsn-unresolved-sheet-reference',
                        message: `[unresolved-sheet-reference] ${partInst.Reference}: schematic view "${partInst.ContentsViewName}" did not resolve to a sub-sheet; ${keepsPhysicalPart ?
                            'the placement records pins and a PCB footprint, so its physical part is kept'
                            : 'the placement stays a hierarchy carrier and contributes no physical part'}`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    });
                    if (keepsPhysicalPart) {
                        yield* this.generateModuleFromPartInst(parent, partInst, instance);
                    }
                }
                continue;
            }
            yield* this.generateModuleFromPartInst(parent, partInst, instance);
        }
    }
    isVerifiedReferenceOnlyHierarchyPart(parent, partInst) {
        if ((0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(partInst)) {
            return true;
        }
        if (partInst.ContentsLibName.length > 0) {
            return false;
        }
        if (partInst.Reference !== '?') {
            return false;
        }
        if (partInst.ContentsViewName.length === 0) {
            return false;
        }
        const matchingSources = new Set(parent.context.allHierarchies
            .filter((hierarchy) => hierarchy.sourceFile !== parent.hierarchyEntry.sourceFile &&
            hierarchy.schematic.Name.toLowerCase() === partInst.ContentsViewName.toLowerCase())
            .map((hierarchy) => hierarchy.sourceFile));
        return matchingSources.size === 1;
    }
    expandsIntoSubSheet(parent, partInst) {
        return parent.instance.children.some((child) => child.sheetSymbol === partInst);
    }
    *reportPhysicalParts(parent, design) {
        if (this.physicalPartResultsEmitted.has(design)) {
            return;
        }
        this.physicalPartResultsEmitted.add(design);
        const physicalParts = this.physicalPartResolver.getIndexForDesign(design);
        for (const part of physicalParts.parts.values()) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-physical-part-unified',
                message: `Unified ${part.sections.length} library packages into component ${part.componentName} for reference ${part.reference}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
            });
            const disagreement = (0, OrcadDsnPhysicalParts_1.findNumberingStyleDisagreement)(design.Cache, part.sections);
            if (disagreement) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-physical-part-numbering-style-disagreement',
                    message: `Sections of reference ${part.reference} disagree on section numbering style: ${disagreement.dissentingSectionName} is ${disagreement.dissentingStyle} but ${disagreement.chosenSectionName} is ${disagreement.chosenStyle}; every placement of ${part.reference} uses the ${disagreement.chosenStyle} style`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                });
            }
        }
        for (const conflict of physicalParts.conflicts) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-physical-part-conflict',
                message: `Reference ${conflict.reference} spans multiple packages but was not unified: ${conflict.message}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
    }
    *generateModuleFromPartInst(parent, partInst, instance) {
        const design = parent.hierarchyEntry.design;
        const dbId = partInst.DbId.toString();
        const moduleRef = `${instance.pathUniqueId}::${dbId}`;
        const instOccurrence = this.findInstOccurrence(instance, dbId);
        const bareReferenceDesignator = instOccurrence?.UserProperty?.find((p) => p.Name === 'Part Reference')?.Value ?? partInst.Reference;
        const fullModuleName = instOccurrence?.UserProperty?.find((p) => p.Name === 'Reference')?.Value ?? partInst.Reference;
        const occurrenceUniqueFullModuleName = fullModuleName === '?' ? `?@${moduleRef}` : fullModuleName;
        const physicalPart = this.physicalPartResolver
            .getIndexForDesign(design)
            .parts.get(this.physicalPartResolver.getPhysicalPartReference(design, partInst));
        const emptyDesignatorSelection = this.resolveEmptyDesignatorPhysicalPartSelection(design, instance, partInst, bareReferenceDesignator);
        if (emptyDesignatorSelection) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: emptyDesignatorSelection.kind === 'inferred' ?
                    'orcad-dsn-empty-designator-physical-part-inferred'
                    : 'orcad-dsn-empty-designator-physical-part-declined',
                message: emptyDesignatorSelection.message,
                severity: emptyDesignatorSelection.kind === 'inferred' ? sigma_engine_shared_1.BoundaryLevel.Recommendation : sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        const referenceDesignator = this.resolvePlacementSectionName(design, partInst, bareReferenceDesignator, physicalPart, emptyDesignatorSelection);
        const symbolResolution = this.resolveSymbolRef(design, partInst, emptyDesignatorSelection);
        if (!symbolResolution) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-module-missing-symbol',
                message: `Could not find symbol for ${partInst.LibName}::${partInst.PkgName}::${partInst.GraphicName} (${referenceDesignator})`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
            });
            return;
        }
        if (symbolResolution.usedFallback) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'symbol-lookup-fallback-physical-part-index',
                message: `Found symbol for ${partInst.LibName}::${partInst.PkgName}::${partInst.GraphicName} (${referenceDesignator}) using backup physical part index`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        const boundingBox = symbolResolution.boundingBox;
        const center = OrcadDsnUtils_1.OrcadDsnUtils.computeModuleCenter(partInst, boundingBox);
        const metadata = {};
        metadata['Value'] = partInst.PartValue;
        for (const userProp of partInst.UserProperty ?? []) {
            metadata[userProp.Name] = userProp.Value;
        }
        const component = new OrcadDsnTransformComponentGenerator_1.OrCadDsnTransformComponent(parent, {
            libName: partInst.LibName,
            packageName: partInst.PkgName,
            libPartCellName: partInst.GraphicName,
            viewName: 'NormalView',
            viewSuffix: symbolResolution.viewSuffix,
            physicalPartSuffix: symbolResolution.physicalPartSuffix,
            packageIndex: symbolResolution.packageIndex,
            sourceDesign: design,
            physicalPartSections: physicalPart?.sections,
        });
        yield component;
        const drawProperties = this.buildDrawProperties(parent, partInst, referenceDesignator);
        const isMultiInstance = instance.totalSheetInstances > 1;
        const moduleProps = {
            ref: moduleRef,
            footprintRef: symbolResolution.symbolRef,
            center,
            transform: this.computeModuleTransform(partInst),
            name: referenceDesignator,
            fullModuleName: occurrenceUniqueFullModuleName,
            drawProperties: drawProperties.props,
            metadata,
            multiInstance: isMultiInstance ?
                {
                    instanceId: parent.designId,
                    displayModuleRef: dbId,
                }
                : undefined,
        };
        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
        const projectModuleIdentity = occurrenceUniqueFullModuleName ?? referenceDesignator;
        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
            name: projectModuleIdentity,
            component,
            value: metadata['Value']?.toString(),
        });
        yield projectModule;
        const transformModule = new TransformModule_1.TransformModule(parent, moduleProps, component, projectModule);
        yield transformModule;
        if (isMultiInstance && instance.instanceIndex === 0) {
            const multiInstanceParent = parent.parent;
            if (multiInstanceParent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
                const displayDrawProperties = this.buildDrawProperties(parent, partInst, partInst.Reference);
                const displayModuleProps = {
                    ref: dbId,
                    footprintRef: symbolResolution.symbolRef,
                    center,
                    transform: this.computeModuleTransform(partInst),
                    name: partInst.Reference,
                    fullModuleName: partInst.Reference,
                    drawProperties: displayDrawProperties.props,
                    metadata,
                };
                yield new TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule(multiInstanceParent, displayModuleProps, component);
            }
        }
        yield* drawProperties.warnings;
        yield* this.createPinConnections(parent, design, partInst, moduleRef, occurrenceUniqueFullModuleName ?? referenceDesignator, boundingBox, emptyDesignatorSelection);
    }
    findInstOccurrence(instance, refInstId) {
        const occurrences = instance.instOccurence.InstOccurrence ?? [];
        return occurrences.find((o) => o.RefInstId === refInstId);
    }
    resolveEmptyDesignatorPhysicalPartSelection(design, instance, partInst, reference) {
        const placementKey = `${instance.pathUniqueId}::${partInst.DbId}`;
        const cached = this.emptyDesignatorSelections.get(placementKey);
        if (cached) {
            return cached;
        }
        if (partInst.DeviceDesignator !== '') {
            return undefined;
        }
        const resolution = design.Cache && OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(design.Cache, partInst);
        const libPart = resolution?.pkg.LibPart.find((candidate) => !!candidate.CellName && OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, candidate.CellName));
        const physicalParts = libPart?.PhysicalPart ?? [];
        if (!resolution || physicalParts.length < 2) {
            return undefined;
        }
        const schematic = instance.hierarchyEntry.schematic;
        const schematicIndex = (design.Schematic ?? []).indexOf(schematic);
        const pageOrdinalOffset = (design.Schematic ?? [])
            .slice(0, Math.max(0, schematicIndex))
            .reduce((total, candidate) => total + (candidate.Page?.length ?? 0), 0);
        const placements = (schematic.Page ?? []).flatMap((page, pageOrdinal) => (page.PartInst ?? [])
            .filter((candidate) => this.physicalPartResolver.getPhysicalPartReference(design, candidate) === reference &&
            candidate.LibName === partInst.LibName &&
            candidate.PkgName === partInst.PkgName &&
            candidate.GraphicName === partInst.GraphicName)
            .map((candidate) => ({
            key: `${instance.pathUniqueId}::${candidate.DbId}`,
            dbId: candidate.DbId,
            pageOrdinal: pageOrdinalOffset + pageOrdinal,
            deviceDesignator: candidate.DeviceDesignator,
            portSurface: [...(candidate.PortInstScalar ?? []), ...(candidate.PortInstBus ?? [])]
                .sort((left, right) => left.Position - right.Position || left.Name.localeCompare(right.Name))
                .map((port) => `${port.Position}:${port.Name}`)
                .join('|'),
        })));
        const explicitClaims = placements
            .filter((candidate) => candidate.deviceDesignator !== '')
            .map((candidate) => this.slottedSectionIndexFromDeviceDesignator(candidate.deviceDesignator, physicalParts.length));
        if (explicitClaims.some((claim) => claim === undefined) || new Set(explicitClaims).size !== explicitClaims.length) {
            return undefined;
        }
        const unclaimed = physicalParts.filter((_, index) => !explicitClaims.includes(index));
        const selection = (0, OrcadDsnPhysicalParts_1.resolveEmptyDesignatorPhysicalPartBijection)(placements, unclaimed);
        for (const [key, value] of selection) {
            if (value.kind === 'inferred') {
                const physicalPartIndex = physicalParts.indexOf(unclaimed[value.physicalPartIndex]);
                this.emptyDesignatorSelections.set(key, { ...value, physicalPartIndex });
            }
            else {
                this.emptyDesignatorSelections.set(key, value);
            }
        }
        return this.emptyDesignatorSelections.get(placementKey);
    }
    resolvePlacementSectionName(design, partInst, bareReferenceDesignator, physicalPart, emptyDesignatorSelection) {
        const cache = design.Cache;
        if (!cache) {
            return bareReferenceDesignator;
        }
        if (physicalPart && physicalPart.sections.length > 1) {
            const sectionIndex = physicalPart.sections.findIndex((section) => section.libName === partInst.LibName && section.packageName === partInst.PkgName);
            const firstSectionResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, physicalPart.sections[0].representativePartInst);
            if (sectionIndex === -1 || !firstSectionResolution) {
                return bareReferenceDesignator;
            }
            return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(firstSectionResolution.pkg.AlphabeticNumbering));
        }
        const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
        if (!resolution) {
            return bareReferenceDesignator;
        }
        const { pkg } = resolution;
        const style = (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(pkg.AlphabeticNumbering);
        const genuineSections = (0, OrcadDsnSectionEmission_1.enumerateGenuineSections)(pkg.LibPart, 'perLibPart');
        if (genuineSections.length <= 1) {
            return bareReferenceDesignator;
        }
        if (pkg.LibPart.length > 1) {
            const matchedLibPart = pkg.LibPart.find((libPart) => !!libPart.CellName && OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName));
            const sectionIndex = matchedLibPart ? genuineSections.findIndex((section) => section.libPart === matchedLibPart) : -1;
            if (sectionIndex === -1) {
                return bareReferenceDesignator;
            }
            return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, style);
        }
        const physicalParts = pkg.LibPart[0]?.PhysicalPart ?? [];
        if (emptyDesignatorSelection?.kind === 'inferred') {
            const matchedPhysicalPart = physicalParts[emptyDesignatorSelection.physicalPartIndex];
            const sectionIndex = genuineSections.findIndex((section) => section.physicalPart === matchedPhysicalPart);
            if (sectionIndex !== -1) {
                return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, style);
            }
        }
        const rawIndex = this.slottedSectionIndexFromDeviceDesignator(partInst.DeviceDesignator, physicalParts.length);
        if (rawIndex !== undefined) {
            const matchedPhysicalPart = physicalParts[rawIndex];
            const sectionIndex = genuineSections.findIndex((section) => section.physicalPart === matchedPhysicalPart);
            if (sectionIndex !== -1) {
                return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, style);
            }
        }
        return bareReferenceDesignator;
    }
    slottedSectionIndexFromDeviceDesignator(deviceDesignator, sectionCount) {
        if ((0, alphaDesignatorToConstant_1.isAlphaDesignator)(deviceDesignator)) {
            const sectionIndex = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(deviceDesignator) - 1;
            return sectionIndex >= 0 && sectionIndex < sectionCount ? sectionIndex : undefined;
        }
        if (/^-?\d+$/.test(deviceDesignator)) {
            const numericDesignator = parseInt(deviceDesignator, 10);
            if (numericDesignator !== 0) {
                const sectionIndex = Math.abs(numericDesignator) - 1;
                return sectionIndex >= 0 && sectionIndex < sectionCount ? sectionIndex : undefined;
            }
        }
        return undefined;
    }
    resolveSymbolRef(design, partInst, emptyDesignatorSelection) {
        const cache = design.Cache;
        if (!cache) {
            return undefined;
        }
        const packageResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
        if (!packageResolution) {
            return undefined;
        }
        const { pkg, packageIndex } = packageResolution;
        const inferredPhysicalPartIndex = emptyDesignatorSelection?.kind === 'inferred' ? emptyDesignatorSelection.physicalPartIndex : undefined;
        for (const libPart of pkg.LibPart) {
            for (const normalView of libPart.NormalView ?? []) {
                const physicalParts = libPart.PhysicalPart ?? [];
                for (const physicalPart of physicalParts) {
                    const physIdx = physicalParts.indexOf(physicalPart);
                    const physSuffix = physicalParts.length > 1 ? `-${physIdx + 1}` : '';
                    const ref = `${libPart.CellName}${normalView.Suffix}${physSuffix}`;
                    if (ref === `${partInst.GraphicName}${partInst.DeviceDesignator}`) {
                        return {
                            symbolRef: ref,
                            boundingBox: normalView.BoundingBox,
                            viewSuffix: normalView.Suffix,
                            physicalPartSuffix: physSuffix,
                            packageIndex,
                            usedFallback: false,
                        };
                    }
                }
            }
        }
        if (inferredPhysicalPartIndex !== undefined) {
            for (const libPart of pkg.LibPart.filter((candidate) => !!candidate.CellName && OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, candidate.CellName))) {
                for (const normalView of libPart.NormalView ?? []) {
                    if ((libPart.PhysicalPart?.length ?? 0) > inferredPhysicalPartIndex) {
                        const ref = `${libPart.CellName}${normalView.Suffix}-${inferredPhysicalPartIndex + 1}`;
                        return {
                            symbolRef: ref,
                            boundingBox: normalView.BoundingBox,
                            viewSuffix: normalView.Suffix,
                            physicalPartSuffix: `-${inferredPhysicalPartIndex + 1}`,
                            packageIndex,
                            usedFallback: false,
                        };
                    }
                }
            }
        }
        for (const libPart of pkg.LibPart) {
            for (const normalView of libPart.NormalView ?? []) {
                const ref = `${libPart.CellName}${normalView.Suffix}`;
                if (ref === partInst.GraphicName && (libPart.PhysicalPart?.length ?? 0) <= 1) {
                    return {
                        symbolRef: ref,
                        boundingBox: normalView.BoundingBox,
                        viewSuffix: normalView.Suffix,
                        physicalPartSuffix: '',
                        packageIndex,
                        usedFallback: false,
                    };
                }
            }
        }
        if ((0, alphaDesignatorToConstant_1.isAlphaDesignator)(partInst.DeviceDesignator)) {
            const physicalPartIndex = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(partInst.DeviceDesignator);
            for (const libPart of pkg.LibPart) {
                for (const normalView of libPart.NormalView ?? []) {
                    if ((libPart.PhysicalPart?.length ?? 0) > 1) {
                        const ref = `${libPart.CellName}${normalView.Suffix}-${physicalPartIndex}`;
                        return {
                            symbolRef: ref,
                            boundingBox: normalView.BoundingBox,
                            viewSuffix: normalView.Suffix,
                            physicalPartSuffix: `-${physicalPartIndex}`,
                            packageIndex,
                            usedFallback: true,
                        };
                    }
                }
            }
        }
        const numericDesignator = parseInt(partInst.DeviceDesignator, 10);
        if (!isNaN(numericDesignator) && numericDesignator !== 0) {
            const physicalPartIndex = Math.abs(numericDesignator);
            for (const libPart of pkg.LibPart) {
                for (const normalView of libPart.NormalView ?? []) {
                    if ((libPart.PhysicalPart?.length ?? 0) > 1) {
                        const ref = `${libPart.CellName}${normalView.Suffix}-${physicalPartIndex}`;
                        return {
                            symbolRef: ref,
                            boundingBox: normalView.BoundingBox,
                            viewSuffix: normalView.Suffix,
                            physicalPartSuffix: `-${physicalPartIndex}`,
                            packageIndex,
                            usedFallback: true,
                        };
                    }
                }
            }
        }
        for (const libPart of pkg.LibPart) {
            for (const normalView of libPart.NormalView ?? []) {
                const ref = `${libPart.CellName}${normalView.Suffix}`;
                if (ref === partInst.GraphicName && (libPart.PhysicalPart?.length ?? 0) > 1) {
                    return {
                        symbolRef: `${ref}-1`,
                        boundingBox: normalView.BoundingBox,
                        viewSuffix: normalView.Suffix,
                        physicalPartSuffix: '-1',
                        packageIndex,
                        usedFallback: true,
                    };
                }
            }
        }
        return undefined;
    }
    computeModuleTransform(inst) {
        const { angle, mirrored } = OrcadDsnUtils_1.OrcadDsnUtils.orcadRotationToCadstrom(inst.Rotation, inst.Mirror);
        return {
            pos: [0, 0],
            angle,
            mirrored,
        };
    }
    buildDrawProperties(parent, partInst, referenceDesignator) {
        const drawProperties = {};
        const warnings = [];
        const namedValues = {
            'Part Reference': referenceDesignator,
            Value: partInst.PartValue,
        };
        for (const userProp of partInst.UserProperty ?? []) {
            namedValues[userProp.Name] = userProp.Value;
        }
        for (const prop of partInst.DisplayProperty ?? []) {
            let layerType = sigma_engine_shared_1.SchLayerType.Label;
            switch (prop.Name) {
                case 'Name':
                    layerType = sigma_engine_shared_1.SchLayerType.NameLabel;
                    break;
                case 'Value':
                    layerType = sigma_engine_shared_1.SchLayerType.ValueLabel;
                    break;
            }
            const namedValue = namedValues[prop.Name];
            if (namedValue === undefined) {
                warnings.push(new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-module-missing-draw-property-value',
                    message: `Could not find value for defined display property ${prop.Name} on ${partInst.Reference}`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                }));
                continue;
            }
            drawProperties[prop.Name] = {
                ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
                pos: [prop.LocX + partInst.LocX, prop.LocY + partInst.LocY],
                layer: orcadLayers_1.OrCadSchLayerMap[layerType].id,
                angle: prop.Rotation * 90,
                justify: [-1, -1],
                size: this.scaleFont(prop.Font),
                thickness: 1,
                hidden: false,
                ref_designator: prop.Name === 'Part Reference',
                mirror: [false, false],
            };
        }
        return { props: drawProperties, warnings };
    }
    *createPinConnections(parent, design, partInst, moduleRef, moduleName, boundingBox, emptyDesignatorSelection) {
        const portInsts = [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])];
        const cachePinPositionMap = this.buildPinPositionMapForPartInst(design, partInst, emptyDesignatorSelection);
        const pinPositionMap = pinPositionMapWithLiveFallback(cachePinPositionMap, portInsts);
        const pinRefByPosition = (0, orcadPinRefs_1.buildPinRefByPosition)(pinPositionMap);
        const symbolPinResolution = this.buildSymbolPinPositions(design, partInst);
        const symbolPinPositions = symbolPinResolution?.positions;
        if (symbolPinResolution !== undefined && !symbolPinResolution.placedViewResolved) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-placed-view-unresolved',
                message: `[placed-view-unresolved] ${partInst.Reference}: graphic name "${partInst.GraphicName}" matches none of the cached views, so recorded pin positions stand unchallenged`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        const permutedRecordedPins = symbolPinResolution?.placedViewResolved === true ?
            this.findPermutedRecordedPins(parent.hierarchyEntry.page, partInst, portInsts, symbolPinPositions, boundingBox)
            : new Set();
        if (permutedRecordedPins.size > 0 &&
            this.recordedPinMismatchDiagnosticsEmitted <
                OrCadDsnTransformModuleGenerator.RECORDED_PIN_MISMATCH_DIAGNOSTIC_LIMIT) {
            this.recordedPinMismatchDiagnosticsEmitted++;
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-recorded-pin-provenance-mismatch',
                message: `[recorded-pin-provenance-mismatch] ${partInst.Reference}: ${permutedRecordedPins.size} recorded pin positions coincide with different wired pins on the same placement; using symbol geometry for those pins`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        for (const portInst of portInsts) {
            if (portInst.RequiresRecordedPosition === true &&
                (portInst.RecordedX === undefined || portInst.RecordedY === undefined)) {
                continue;
            }
            const pinNumber = pinPositionMap.get(portInst.Position);
            if (pinNumber === undefined) {
                continue;
            }
            const effectivePinRef = pinRefByPosition.get(portInst.Position) ?? pinNumber;
            let pinPosition;
            const recordedPinPosition = permutedRecordedPins.has(portInst.Position) ? {} : this.validRecordedPinPosition(parent, partInst, portInst);
            if (recordedPinPosition.mismatch &&
                this.recordedPinMismatchDiagnosticsEmitted <
                    OrCadDsnTransformModuleGenerator.RECORDED_PIN_MISMATCH_DIAGNOSTIC_LIMIT) {
                this.recordedPinMismatchDiagnosticsEmitted++;
                const { wireDbId, position } = recordedPinPosition.mismatch;
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-recorded-pin-mismatch',
                    message: `[recorded-pin-mismatch] ${partInst.Reference} pin ${portInst.Name || portInst.Position}: recorded (${position.join(', ')}) does not lie on page wire ${wireDbId}`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
            }
            if (portInst.RequiresRecordedPosition === true && !recordedPinPosition.position) {
                continue;
            }
            if (recordedPinPosition.position) {
                pinPosition = recordedPinPosition.position;
            }
            else {
                const symbolPin = symbolPinPositions?.get(portInst.Position);
                if (symbolPin) {
                    pinPosition = OrcadDsnUtils_1.OrcadDsnUtils.transformPinToSchematicCoords(symbolPin, partInst, boundingBox);
                }
                else {
                    pinPosition = [portInst.HotptX + partInst.LocX, portInst.HotptY + partInst.LocY];
                }
            }
            const isPowerPin = portInst.Type === 7 && !!portInst.Name;
            const appliesNetName = isPowerPin ? { label: portInst.Name, labelType: 'power-pin' } : undefined;
            const skipPositionalCombobulation = portInst.Visible === false;
            yield new TransformModulePinConnection_1.TransformModulePinConnection(parent, {
                position: pinPosition,
                connection: {
                    moduleRef,
                    moduleName,
                    pinRef: effectivePinRef,
                    pinNumber,
                    pinPosition: portInst.Position,
                    appliesNetName,
                    skipPositionalCombobulation,
                },
            });
        }
    }
    findPermutedRecordedPins(page, partInst, portInsts, symbolPinPositions, boundingBox) {
        const geometryByPoint = new Map();
        const geometryByPosition = new Map();
        for (const portInst of portInsts) {
            if (!symbolPinPositions?.has(portInst.Position)) {
                continue;
            }
            const geometry = this.geometryPinPosition(partInst, portInst, symbolPinPositions, boundingBox);
            geometryByPosition.set(portInst.Position, geometry);
            const key = geometry.join(',');
            const positions = geometryByPoint.get(key) ?? new Set();
            positions.add(portInst.Position);
            geometryByPoint.set(key, positions);
        }
        const collisions = new Set();
        for (const portInst of portInsts) {
            if (portInst.RecordedWireDbId === undefined ||
                portInst.RecordedX === undefined ||
                portInst.RecordedY === undefined) {
                continue;
            }
            const recorded = [portInst.RecordedX, portInst.RecordedY];
            const ownGeometry = geometryByPosition.get(portInst.Position);
            if (!ownGeometry || (recorded[0] === ownGeometry[0] && recorded[1] === ownGeometry[1])) {
                continue;
            }
            const positionsAtRecordedPoint = geometryByPoint.get(recorded.join(','));
            if (positionsAtRecordedPoint &&
                Array.from(positionsAtRecordedPoint).some((position) => position !== portInst.Position)) {
                collisions.add(portInst.Position);
            }
        }
        if (collisions.size < 2) {
            return new Set();
        }
        return new Set(Array.from(collisions).filter((position) => {
            const geometry = geometryByPosition.get(position);
            return geometry !== undefined && this.pageContainsPoint(page, geometry);
        }));
    }
    geometryPinPosition(partInst, portInst, symbolPinPositions, boundingBox) {
        const symbolPin = symbolPinPositions?.get(portInst.Position);
        return symbolPin ?
            OrcadDsnUtils_1.OrcadDsnUtils.transformPinToSchematicCoords(symbolPin, partInst, boundingBox)
            : [portInst.HotptX + partInst.LocX, portInst.HotptY + partInst.LocY];
    }
    validRecordedPinPosition(parent, partInst, portInst) {
        if (portInst.RecordedX === undefined || portInst.RecordedY === undefined) {
            return {};
        }
        const recordedPosition = [portInst.RecordedX, portInst.RecordedY];
        if (portInst.RecordedWireDbId !== undefined) {
            const wire = this.findPageWire(parent.hierarchyEntry.page, portInst.RecordedWireDbId);
            if (wire && this.wireContainsPoint(wire, recordedPosition)) {
                return { position: recordedPosition };
            }
            return { mismatch: { wireDbId: portInst.RecordedWireDbId, position: recordedPosition } };
        }
        const placementIsNearOrigin = Math.abs(partInst.LocX) <= 1 && Math.abs(partInst.LocY) <= 1;
        return {
            position: recordedPosition[0] === 0 && recordedPosition[1] === 0 && !placementIsNearOrigin ? undefined : recordedPosition,
        };
    }
    findPageWire(page, dbId) {
        for (const net of page.NetScalar ?? []) {
            const wire = net.WireScalars?.find((candidate) => candidate.DbId === dbId);
            if (wire) {
                return wire;
            }
        }
        for (const net of page.NetBus ?? []) {
            const wire = net.WireBus?.find((candidate) => candidate.DbId === dbId);
            if (wire) {
                return wire;
            }
            for (const scalarNet of net.NetScalar ?? []) {
                const scalarWire = scalarNet.WireScalars?.find((candidate) => candidate.DbId === dbId);
                if (scalarWire) {
                    return scalarWire;
                }
            }
        }
        return undefined;
    }
    wireContainsPoint(wire, [x, y]) {
        const crossProduct = (x - wire.StartX) * (wire.EndY - wire.StartY) - (y - wire.StartY) * (wire.EndX - wire.StartX);
        return (crossProduct === 0 &&
            x >= Math.min(wire.StartX, wire.EndX) &&
            x <= Math.max(wire.StartX, wire.EndX) &&
            y >= Math.min(wire.StartY, wire.EndY) &&
            y <= Math.max(wire.StartY, wire.EndY));
    }
    pageContainsPoint(page, point) {
        for (const net of page.NetScalar ?? []) {
            if (net.WireScalars?.some((wire) => this.wireContainsPoint(wire, point))) {
                return true;
            }
        }
        for (const bus of page.NetBus ?? []) {
            if (bus.WireBus?.some((wire) => this.wireContainsPoint(wire, point))) {
                return true;
            }
            for (const net of bus.NetScalar ?? []) {
                if (net.WireScalars?.some((wire) => this.wireContainsPoint(wire, point))) {
                    return true;
                }
            }
        }
        return false;
    }
    buildPinPositionMapForPartInst(design, partInst, emptyDesignatorSelection) {
        const cache = design.Cache;
        if (!cache) {
            return undefined;
        }
        const packageResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
        if (!packageResolution) {
            return undefined;
        }
        const { pkg } = packageResolution;
        for (const libPart of pkg.LibPart) {
            if (!libPart.CellName || !OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
                continue;
            }
            let physicalPart;
            if (emptyDesignatorSelection?.kind === 'inferred') {
                physicalPart = libPart.PhysicalPart?.[emptyDesignatorSelection.physicalPartIndex];
            }
            else if ((0, alphaDesignatorToConstant_1.isAlphaDesignator)(partInst.DeviceDesignator) && (libPart.PhysicalPart?.length ?? 0) > 1) {
                const idx = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(partInst.DeviceDesignator) - 1;
                physicalPart = libPart.PhysicalPart?.[idx];
            }
            else {
                const numericIdx = /^-?\d+$/.test(partInst.DeviceDesignator) ? parseInt(partInst.DeviceDesignator, 10) : NaN;
                if (!isNaN(numericIdx) && numericIdx !== 0 && (libPart.PhysicalPart?.length ?? 0) > 1) {
                    physicalPart = libPart.PhysicalPart?.[Math.abs(numericIdx) - 1];
                }
                else {
                    physicalPart = libPart.PhysicalPart?.[0];
                }
            }
            if (!physicalPart) {
                continue;
            }
            return (0, OrcadDsnSectionEmission_1.buildPinPositionMap)(physicalPart);
        }
        return undefined;
    }
    buildSymbolPinPositions(design, partInst) {
        const cache = design.Cache;
        if (!cache) {
            return undefined;
        }
        const packageResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
        if (!packageResolution) {
            return undefined;
        }
        const { pkg } = packageResolution;
        const placedViewSuffix = (0, OrcadDsnUtils_1.placedViewSuffixOf)(partInst.GraphicName);
        let fallback;
        for (const libPart of pkg.LibPart) {
            if (libPart.CellName === undefined ||
                !OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
                continue;
            }
            for (const view of [...(libPart.NormalView ?? []), ...(libPart.ConvertView ?? [])]) {
                const map = symbolPinPositionsOfView(view);
                if (view.Suffix === placedViewSuffix) {
                    return { positions: map, placedViewResolved: true };
                }
                fallback ??= map;
            }
        }
        if (fallback === undefined) {
            return undefined;
        }
        return { positions: fallback, placedViewResolved: placedViewSuffix === undefined };
    }
    scaleFont(font) {
        return Math.abs(font.Height / 2);
    }
}
exports.OrCadDsnTransformModuleGenerator = OrCadDsnTransformModuleGenerator;
//# sourceMappingURL=OrcadDsnTransformModuleGenerator.js.map